import type {
  RecentJournalFeedEntry,
  RecentProductJournalFeedEntry,
} from "../../api/WarehouseApi";
import { Action } from "../../types/entities";

export interface PendingTakenReminder {
  productId: string;
  productName: string;
  sourceBoxId: string;
  sourceBoxName: string;
  nextAtMs: number;
  stepIndex: number;
}

export interface ReminderSnapshot {
  productId: string;
  productName: string;
  sourceBoxId: string;
  sourceBoxName: string;
  nextAtMs: number;
  stepIndex: number;
}

export const REMINDERS_STORAGE_KEY = "korobochka.reminders.taken.v1";

export const REMINDER_STEPS_MS = [
  3 * 60 * 60 * 1000,
  Math.floor(1.5 * 60 * 60 * 1000),
  45 * 60 * 1000,
  20 * 60 * 1000,
];

export const MAX_REMINDERS = 200;

export function parseReminderSnapshot(raw: string | null): ReminderSnapshot[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    const snapshots: ReminderSnapshot[] = [];

    parsed.forEach((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return;
      }

      const source = item as Record<string, unknown>;
      const productId =
        typeof source.productId === "string" ? source.productId.trim() : "";
      const productName =
        typeof source.productName === "string" ? source.productName.trim() : "";
      const sourceBoxId =
        typeof source.sourceBoxId === "string" ? source.sourceBoxId.trim() : "";
      const sourceBoxName =
        typeof source.sourceBoxName === "string"
          ? source.sourceBoxName.trim()
          : "";
      const nextAtMs =
        typeof source.nextAtMs === "number" && Number.isFinite(source.nextAtMs)
          ? source.nextAtMs
          : 0;
      const stepIndex =
        typeof source.stepIndex === "number" &&
        Number.isFinite(source.stepIndex)
          ? Math.max(
              0,
              Math.min(
                REMINDER_STEPS_MS.length - 1,
                Math.floor(source.stepIndex),
              ),
            )
          : 0;

      if (!productId) {
        return;
      }

      snapshots.push({
        productId,
        productName: productName || productId,
        sourceBoxId,
        sourceBoxName: sourceBoxName || sourceBoxId || "неизвестная коробка",
        nextAtMs: nextAtMs > 0 ? nextAtMs : Date.now() + REMINDER_STEPS_MS[0],
        stepIndex,
      });
    });

    return snapshots.slice(0, MAX_REMINDERS);
  } catch {
    return [];
  }
}

export function normalizeFeedDateToMs(raw: unknown): number {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return Date.now();
  }

  const source = raw as Record<string, unknown>;
  const year = Number(source.year);
  const month = Number(source.month);
  const day = Number(source.day);
  const hour = Number(source.hour || 0);
  const minute = Number(source.minute || 0);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return Date.now();
  }

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (Number.isNaN(date.getTime())) {
    return Date.now();
  }

  return date.getTime();
}

export function isRecentProductJournalEntry(
  entry: RecentJournalFeedEntry,
): entry is RecentProductJournalFeedEntry {
  return entry.eventType !== "box.restored";
}

export function isBoxEntityJournalEntry(
  entry: RecentProductJournalFeedEntry,
): boolean {
  return entry.productId.trim().startsWith("box_");
}

export function resolveEntityNameForNotification(
  entry: RecentProductJournalFeedEntry,
): string {
  const name = (entry.productName || "").trim();
  if (name) {
    return name;
  }

  const id = entry.productId.trim();
  if (id) {
    return id;
  }

  return isBoxEntityJournalEntry(entry)
    ? "неизвестная коробка"
    : "неизвестный товар";
}

export function resolveBoxNameForNotification(
  entry: RecentProductJournalFeedEntry,
): string {
  const name = (entry.boxName || "").trim();
  if (name) {
    return name;
  }

  const id = entry.fromTo.trim();
  return id || "неизвестная коробка";
}

export function buildInitialReminder(
  entry: RecentProductJournalFeedEntry,
  now = Date.now(),
): PendingTakenReminder {
  const productId = entry.productId.trim();
  const productName = resolveEntityNameForNotification(entry);
  const sourceBoxId = entry.fromTo.trim();
  const sourceBoxName = resolveBoxNameForNotification(entry);
  const actionAtMs = normalizeFeedDateToMs(entry.date);
  const firstDelay = REMINDER_STEPS_MS[0];
  const nextAtMs =
    actionAtMs + firstDelay > now ? actionAtMs + firstDelay : now + firstDelay;

  return {
    productId,
    productName,
    sourceBoxId,
    sourceBoxName,
    stepIndex: 0,
    nextAtMs,
  };
}

export function buildNextReminder(
  reminder: PendingTakenReminder,
): PendingTakenReminder {
  const nextStepIndex = (reminder.stepIndex + 1) % REMINDER_STEPS_MS.length;
  const nextDelay = REMINDER_STEPS_MS[nextStepIndex];

  return {
    ...reminder,
    stepIndex: nextStepIndex,
    nextAtMs: Date.now() + nextDelay,
  };
}

export function isReminderClearingAction(
  entry: RecentProductJournalFeedEntry,
): boolean {
  return entry.action === Action.put || entry.action === Action.destroy;
}
